import React, { useState, useEffect, useCallback } from 'react';
import BaseModel from '../../BaseModal';
import { FlexWrapperCenter } from '../../styles/WrapperStyles';
import styled from 'styled-components';
import { getDateWithTime } from '../../utils';
import urls from '../../api/urls.json';
import { authorizedHttpGet } from '../../api/httpCalls';
import './Modal.css';

const tagToType = {
    'application_image': 'Application Image',
    'updated_image': 'Updated Image',
    'selfie_image': 'Selfie Image',
    'audio_prompt': 'Audio Prompt',
    'gesture_image': 'Gesture Image',
    'invoice_pdf': 'Invoice PDF',
    'event_image': 'Event Image',
    'no_data': 'No Data'
};

const ParentDiv = styled.div`
  width: 800px;
  height: 400px;
  border-radius: 20px;
  overflow-y: auto;
`;

const TitleView = styled.div`
  font-family: 'Gilroy-Bold';
  font-size: 20px;
  font-weight: 800;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #484848;
  text-align: center;
  margin-bottom: 20px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
  background-color: #f2f2f2;
  font-family: 'Gilory-Semi';
  font-size: 18px;
  font-weight: 600;
`;

const Td = styled.td`
  border: 1px solid #ddd;
  padding: 8px;
  font-family: 'Gilory-Semi';
  font-size: 18px;
`;

function ImageHistoryModal(props) {
    const [data, setData] = useState([]);
    const [error, setError] = useState(false);

    const processData = useCallback((imageHistory, fallbackSelfieUrl) => {
        const normalizedHistory = Array.isArray(imageHistory) ? [...imageHistory] : [];
        const hasSelfieInHistory = normalizedHistory.some((item) => item?.image_url === fallbackSelfieUrl);
        if (fallbackSelfieUrl && !hasSelfieInHistory) {
            normalizedHistory.unshift({
                image_url: fallbackSelfieUrl,
                created_at: null,
                tag: 'selfie_image'
            });
        }

        const processedData = normalizedHistory.map(item => ({
            ...item,
            image_type: tagToType[item.tag] || 'Unknown'
        }));
        const dedupeSet = new Set();
        const dedupedData = processedData.filter((item) => {
            const key = `${item.image_url}::${item.tag}::${item.created_at || ''}`;
            if (dedupeSet.has(key)) return false;
            dedupeSet.add(key);
            return true;
        });

        return dedupedData.sort((a, b) => {
            const firstDate = a.created_at ? new Date(a.created_at) : null;
            const secondDate = b.created_at ? new Date(b.created_at) : null;
            const firstTs = firstDate && !Number.isNaN(firstDate.getTime()) ? firstDate.getTime() : 0;
            const secondTs = secondDate && !Number.isNaN(secondDate.getTime()) ? secondDate.getTime() : 0;
            return secondTs - firstTs;
        });
    }, []);

    const fetchImageHistory = useCallback(async () => {
        try {
            const response = await authorizedHttpGet(urls.GET_IMAGE_HISTORY, { ref: props.userRef });
            if (response.status && response.data) {
                const imageHistory = response.data.image_history || [];
                const selfieUrl = response.data.selfie_url || null;
                setData(processData(imageHistory, selfieUrl));
                setError(false);
            } else {
                setData([]);
                setError(false);
            }
        } catch (err) {
            setError(true);
        }
    }, [props.userRef, processData]);

    useEffect(() => {
        if (props.display) {
            fetchImageHistory();
        }
    }, [props.display, fetchImageHistory]);

    return (
        <BaseModel closeModal={props.closeModal}>
            <ParentDiv>
                <FlexWrapperCenter>
                    <TitleView>Image History</TitleView>
                </FlexWrapperCenter>
                {error ? (
                    <FlexWrapperCenter>
                        <div style={{ color: 'red', padding: '16px', textAlign: 'center' }}>
                            Error occurred while fetching image history.
                        </div>
                    </FlexWrapperCenter>
                ) : data.length === 0 ? (
                    <FlexWrapperCenter>
                        <div style={{ color: '#484848', padding: '16px', textAlign: 'center' }}>
                            No image history found.
                        </div>
                    </FlexWrapperCenter>
                ) : (
                    <Table>
                        <thead>
                            <tr>
                                <Th>Datetime (created_at)</Th>
                                <Th>Image URL</Th>
                                <Th>Image Type</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((item, index) => {
                                const filename = item.image_url.split('/').pop();
                                const displayUrl = filename || item.image_url;
                                return (
                                    <tr key={index}>
                                        <Td>{item.created_at ? getDateWithTime(item.created_at) : '-'}</Td>
                                        <Td>
                                            <a href={item.image_url} target="_blank" rel="noopener noreferrer" title={item.image_url}>
                                                {displayUrl.length > 30 ? displayUrl.substring(0, 30) + '...' : displayUrl}
                                            </a>
                                        </Td>
                                        <Td>{item.image_type}</Td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                )}
            </ParentDiv>
        </BaseModel>
    );
}

export default ImageHistoryModal;
